import { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { DragDropContext, Droppable, Draggable, DropResult, DragStart } from '@hello-pangea/dnd';
import { format } from 'date-fns';
import { Sparkles, ChevronDown, PanelRightOpen } from 'lucide-react';
import { Task, Tag, Folder } from '@/types/task';
import { TaskItem } from '@/components/TaskItem';
import { FolderGroupedList } from '@/components/FolderGroupedList';
import { FolderManager } from '@/components/FolderManager';
import { CompletedTaskItem } from '@/components/CompletedTaskItem';
import { AddTaskForm } from '@/components/AddTaskForm';
import { StatusUpdate } from '@/components/StatusUpdate';
import { ProgressRing } from '@/components/ProgressRing';
import { MainTaskZone } from '@/components/MainTaskZone';
import { BacklogSidebar } from '@/components/BacklogSidebar';
import { BacklogPanel } from '@/components/BacklogPanel';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useHaptic } from '@/hooks/useHaptic';

interface TodayViewWithBacklogProps {
  tasks: Task[];
  backlogTasks: Task[];
  tags: Tag[];
  folders: Folder[];
  status: string | null;
  onAddTask: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  onAddBacklogTask: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  onAddSideQuest: (content: string, duration?: number, dueTime?: string) => Promise<void>;
  onToggleTask: (id: string) => void;
  onDeleteTask: (id: string) => void;
  onReorderTasks: (tasks: Task[]) => void;
  onUpdateStatus: (content: string) => Promise<void>;
  onMoveToToday: (taskId: string) => void;
  onMoveToBacklog: (taskId: string) => void;
  onSetMainTask: (taskId: string) => void;
  onClearMainTask: () => void;
  onUpdateTask: (id: string, updates: Partial<Task>) => void;
  onCreateTag: (name: string, color: string) => Promise<Tag | null>;
  onCreateFolder: (name: string, color: string) => Promise<Folder | null>;
  onUpdateFolder?: (id: string, updates: Partial<Pick<Folder, 'name' | 'color'>>) => Promise<void>;
  onDeleteFolder?: (id: string) => Promise<void>;
}

export function TodayViewWithBacklog({
  tasks,
  backlogTasks,
  tags,
  folders,
  status,
  onAddTask,
  onAddBacklogTask,
  onAddSideQuest,
  onToggleTask,
  onDeleteTask,
  onReorderTasks,
  onUpdateStatus,
  onMoveToToday,
  onMoveToBacklog,
  onSetMainTask,
  onClearMainTask,
  onUpdateTask,
  onCreateTag,
  onCreateFolder,
  onUpdateFolder,
  onDeleteFolder,
}: TodayViewWithBacklogProps) {
  const isMobile = useIsMobile();
  const { triggerLight, triggerDrop } = useHaptic();
  const [isCompletedOpen, setIsCompletedOpen] = useState(false);
  const [isBacklogOpen, setIsBacklogOpen] = useState(() => {
    const saved = localStorage.getItem('backlog-drawer-open');
    return saved ? JSON.parse(saved) : false;
  });
  const [isDraggingFromBacklog, setIsDraggingFromBacklog] = useState(false);
  
  // Persist drawer state and notify listeners (replaces polling in Index)
  useEffect(() => {
    localStorage.setItem('backlog-drawer-open', JSON.stringify(isBacklogOpen));
    window.dispatchEvent(new CustomEvent('backlog-state-change', { detail: { open: isBacklogOpen } }));
  }, [isBacklogOpen]);
  
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // Separate main task from other active tasks
  const mainTask = useMemo(() => tasks.find(t => t.is_main_task && !t.completed), [tasks]);
  const activeTasks = useMemo(
    () => tasks.filter((t) => !t.completed && !t.is_main_task).sort((a, b) => a.order_index - b.order_index),
    [tasks]
  );
  const completedTasks = useMemo(() => tasks.filter((t) => t.completed), [tasks]);
  const activeBacklogTasks = useMemo(
    () => backlogTasks.filter(t => !t.completed).sort((a, b) => a.order_index - b.order_index),
    [backlogTasks]
  );

  const completedCount = completedTasks.length;
  const totalCount = tasks.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  const handleReviveTask = (id: string) => {
    onToggleTask(id);
  };

  const handleDragStart = (start: DragStart) => {
    triggerLight(); // Haptic "pickup" feedback
    // Track if drag started from backlog so we can disable main/today droppables
    setIsDraggingFromBacklog(start.source.droppableId === 'backlog');
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination, draggableId } = result;

    // Reset backlog drag tracking
    setIsDraggingFromBacklog(false);

    if (!destination) return;

    triggerDrop(); // Haptic "drop" feedback

    // Same location - no change needed
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;

    // Helper to check if a droppable is a today-side droppable (including folder groups)
    const isTodayDroppable = (id: string) => id === 'today-tasks' || id.startsWith('today-folder-');

    // All Todos panel: never move backlog tasks to main/today via drag (use + button only)
    if (source.droppableId === 'backlog' && (destination.droppableId === 'main-task' || isTodayDroppable(destination.droppableId))) {
      return;
    }

    // Reorder within backlog (same as Today list behavior)
    if (source.droppableId === 'backlog' && destination.droppableId === 'backlog') {
      const items = Array.from(activeBacklogTasks);
      const [reorderedItem] = items.splice(source.index, 1);
      items.splice(destination.index, 0, reorderedItem);
      onReorderTasks(items);
      return;
    }

    // Moving from today/main into backlog = move to backlog (append)
    if (destination.droppableId === 'backlog' && (source.droppableId === 'main-task' || isTodayDroppable(source.droppableId))) {
      if (source.droppableId === 'main-task') onClearMainTask();
      onMoveToBacklog(draggableId);
      return;
    }

    // Handle main/today zone movements
    if (destination.droppableId === 'main-task') {
      onSetMainTask(draggableId);
      return;
    }
    if (isTodayDroppable(destination.droppableId)) {
      if (source.droppableId === 'main-task') {
        onClearMainTask();
      }
      const movingTask = [...tasks, ...backlogTasks].find(t => t.id === draggableId);
      if (!movingTask) return;
      const tasksWithoutMoving = activeTasks.filter(t => t.id !== draggableId);
      tasksWithoutMoving.splice(destination.index, 0, { ...movingTask, is_main_task: false });
      onReorderTasks(tasksWithoutMoving);
    }
  };

  const handleAddTask = async (content: string, duration?: number, dueTime?: string) => {
    await onAddTask(content, duration, dueTime);
  };

  const handleAddSideQuest = async (content: string, duration?: number, dueTime?: string) => {
    await onAddSideQuest(content, duration, dueTime);
  };

  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      {/* Backlog panel in-place (no portal) so DnD drop animation isn't broken by transform/portal */}
      <BacklogPanel open={isBacklogOpen} onClose={() => setIsBacklogOpen(false)}>
        <BacklogSidebar
          tasks={activeBacklogTasks}
          folders={folders}
          onAddTask={onAddBacklogTask}
          onMoveToToday={onMoveToToday}
          onUpdateTask={onUpdateTask}
          onUpdateFolder={onUpdateFolder}
        />
      </BacklogPanel>
      <div className="flex flex-col gap-6 relative pb-24 sm:pb-0">
        {/* Today Focus - Full Width */}
        <div className="flex-1 space-y-6 max-w-3xl mx-auto w-full">
          {/* Header - Day section (styled like progress card) + All Todos right; then progress card with blue accent */}
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            {/* Top section: Today + date, All Todos on the right (no card) */}
            <div className="flex items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold tracking-tight">Today</h1>
                <p className="text-muted-foreground text-sm mt-0.5">
                  {format(new Date(), 'EEEE, MMMM d')}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                {onUpdateFolder && onDeleteFolder && (
                  <FolderManager
                    folders={folders}
                    onAddFolder={onCreateFolder}
                    onUpdateFolder={onUpdateFolder}
                    onDeleteFolder={onDeleteFolder}
                  />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2 text-muted-foreground hover:text-foreground"
                  data-tour-target="backlog-button"
                  onClick={() => setIsBacklogOpen(true)}
                >
                  <PanelRightOpen className="h-4 w-4" />
                  <span>All Todos</span>
                  {activeBacklogTasks.length > 0 && (
                    <span className="text-xs bg-muted px-1.5 py-0.5 rounded-full text-foreground">
                      {activeBacklogTasks.length}
                    </span>
                  )}
                </Button>
              </div>
            </div>

            {/* Progress card - blue accent */}
            {totalCount > 0 ? (
              <div className="rounded-xl bg-muted/40 border border-border/50 p-4 flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Progress
                  </p>
                  <p className="text-xl font-semibold tabular-nums">
                    {completedCount}/{totalCount}
                    <span className="text-primary text-sm font-medium ml-1.5">completed</span>
                  </p>
                  <p className="text-primary text-sm mt-0.5">
                    {progress >= 100
                      ? "All done for today!"
                      : `Keep going, you're ${Math.round(progress)}% there`}
                  </p>
                </div>
                <div className="relative flex-shrink-0">
                  <ProgressRing progress={progress} size={56} hideLabel accentClassName="text-primary" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <span className="text-sm font-semibold">{Math.round(progress)}%</span>
                  </div>
                </div>
              </div>
            ) : null}
          </motion.div>

          {/* Floating Status Update - Desktop only (mobile has it in navbar) */}
          {!isMobile && <StatusUpdate status={status} onUpdate={onUpdateStatus} />}

          {/* Wrapper for tour step 2 - shows both drag source and destination */}
          <div data-tour-target="drag-task-area" className="space-y-6">
            {/* Main Task Zone */}
            <MainTaskZone
              task={mainTask || null}
              tags={tags}
              onToggle={onToggleTask}
              onDelete={onDeleteTask}
              onUpdateTask={onUpdateTask}
              onCreateTag={onCreateTag}
              isDropDisabled={isDraggingFromBacklog}
            />

            {/* Add Task */}
            <AddTaskForm 
              onAdd={handleAddTask} 
              onAddSideQuest={handleAddSideQuest} 
              placeholder="Add a task for today..." 
            />

            {/* Active Tasks List - Grouped by Folder */}
            {activeTasks.length > 0 ? (
              folders.length > 0 ? (
                <FolderGroupedList
                  tasks={activeTasks}
                  folders={folders}
                  tags={tags}
                  droppablePrefix="today"
                  isDraggingFromBacklog={isDraggingFromBacklog}
                  onToggleTask={onToggleTask}
                  onDeleteTask={onDeleteTask}
                  onUpdateTask={onUpdateTask}
                  onMoveToBacklog={onMoveToBacklog}
                  onCreateTag={onCreateTag}
                  onCreateFolder={onCreateFolder}
                  onUpdateFolder={onUpdateFolder}
                />
              ) : (
                <Droppable droppableId="today-tasks" isDropDisabled={isDraggingFromBacklog}>
                  {(provided, snapshot) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                      className={cn(
                        "space-y-2 min-h-[50px] rounded-lg transition-colors",
                        snapshot.isDraggingOver && "bg-primary/5"
                      )}
                    >
                      {activeTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(provided, snapshot) => {
                            const getTransitionStyle = () => {
                              if (snapshot.isDragging && !snapshot.isDropAnimating) return 'none';
                              if (snapshot.isDropAnimating) return undefined;
                              return 'transform 0.2s cubic-bezier(0.2, 0, 0, 1)';
                            };
                            return (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                style={{
                                  ...provided.draggableProps.style,
                                  transition: getTransitionStyle(),
                                }}
                              >
                                <TaskItem
                                  task={task}
                                  tags={tags}
                                  folders={folders}
                                  onToggle={onToggleTask}
                                  onDelete={onDeleteTask}
                                  onUpdateTask={onUpdateTask}
                                  onMoveToBacklog={onMoveToBacklog}
                                  onCreateTag={onCreateTag}
                                  onCreateFolder={onCreateFolder}
                                  isDragging={snapshot.isDragging}
                                  dragHandleProps={provided.dragHandleProps || undefined}
                                />
                              </div>
                            );
                          }}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              )
            ) : !mainTask && completedTasks.length === 0 ? (
              <motion.div
                initial={false}
                animate={{ opacity: 1 }}
                className="text-center py-12"
              >
                <div className="w-12 h-12 rounded-full bg-accent mx-auto mb-4 flex items-center justify-center">
                  <Sparkles className="h-6 w-6 text-accent-foreground" />
                </div>
                <p className="text-muted-foreground">No tasks yet. Add your first task above!</p>
                <p className="text-sm text-muted-foreground/70 mt-1">Or open All Todos to drag tasks here</p>
              </motion.div>
            ) : null}
          </div>

          {/* Completed Tasks Section */}
          {completedTasks.length > 0 && (
            <Collapsible open={isCompletedOpen} onOpenChange={setIsCompletedOpen} className="mt-6">
              <CollapsibleTrigger className="flex items-center gap-2 w-full text-left py-2 group">
                <ChevronDown 
                  className={cn(
                    "h-4 w-4 text-muted-foreground transition-transform duration-200",
                    !isCompletedOpen && "-rotate-90"
                  )} 
                />
                <span className="text-sm font-medium text-muted-foreground">
                  Completed ({completedTasks.length})
                </span>
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-2">
                <div className="space-y-2">
                  {completedTasks.map((task) => (
                    <CompletedTaskItem
                      key={task.id}
                      task={task}
                      onRevive={handleReviveTask}
                    />
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      </div>
    </DragDropContext>
  );
}
